"use client";

import type React from "react";
import { createContext, useContext, useReducer, type ReactNode } from "react";

// Types
interface SyncRule {
  id: string;
  sourceAction: string;
  targetAction: string;
  enabled: boolean;
}

interface SyncConfiguration {
  sourceSystem: string;
  targetSystem: string;
  syncType: "bidirectional" | "one-way";
  description: string;
  rules: {
    creating: SyncRule[];
    deleting: SyncRule[];
  };
}

interface SyncConfigurationPayload {
  rules: {
    creating: SyncRule[];
    deleting: SyncRule[];
  };
  hs_to_sf: {
    create_enabled: boolean;
    update_enabled: boolean;
    delete_enabled: boolean;
    restore_enabled: boolean;
  };
  sf_to_hs: {
    create_enabled: boolean;
    update_enabled: boolean;
    delete_enabled: boolean;
    restore_enabled: boolean;
  };
}

interface ConnectorFlowState {
  currentStep: number;
  selectedConnector: string | null;
  connectionStatus: {
    hubspot: { connected: boolean; tenantId: string | null };
    salesforce: { connected: boolean; tenantId: string | null };
  };
  selectedSyncOption: string | null;
  fieldMappings: Array<{
    id: string;
    hubspotField: string;
    salesforceField: string;
  }>;
  syncConfiguration: SyncConfiguration | null;
  SyncConfigurationPayload: SyncConfigurationPayload | null;
  isValid: boolean;
  errors: Record<string, string>;
}

type ConnectorFlowAction =
  | { type: "SET_STEP"; payload: number }
  | { type: "SET_CONNECTOR"; payload: string }
  | { type: "SET_CONNECTION_STATUS"; payload: any }
  | { type: "SET_SYNC_OPTION"; payload: string }
  | { type: "SET_FIELD_MAPPINGS"; payload: any[] }
  | { type: "SET_SYNC_CONFIGURATION"; payload: SyncConfiguration }
  | { type: "SET_SYNC_CONFIGURATION_PAYLOAD"; payload: SyncConfigurationPayload }
  | {
      type: "SET_VALIDATION";
      payload: { isValid: boolean; errors: Record<string, string> };
    }
  | { type: "RESET_FLOW" };

const initialState: ConnectorFlowState = {
  currentStep: 1,
  selectedConnector: null,
  connectionStatus: {
    hubspot: { connected: false, tenantId: null },
    salesforce: { connected: false, tenantId: null },
  },
  selectedSyncOption: null,
  fieldMappings: [],
  syncConfiguration: null,
  SyncConfigurationPayload : null,
  isValid: false,
  errors: {},
};

const connectorFlowReducer = (
  state: ConnectorFlowState,
  action: ConnectorFlowAction
): ConnectorFlowState => {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, currentStep: action.payload };
    case "SET_CONNECTOR":
      return { ...state, selectedConnector: action.payload };
    case "SET_CONNECTION_STATUS":
      return { ...state, connectionStatus: action.payload };
    case "SET_SYNC_OPTION":
      return { ...state, selectedSyncOption: action.payload };
    case "SET_FIELD_MAPPINGS":
      return { ...state, fieldMappings: action.payload };
    case "SET_SYNC_CONFIGURATION":
      return { ...state, syncConfiguration: action.payload };
    case "SET_SYNC_CONFIGURATION_PAYLOAD":
      return { ...state, SyncConfigurationPayload: action.payload };
    case "SET_VALIDATION":
      return {
        ...state,
        isValid: action.payload.isValid,
        errors: action.payload.errors,
      };
    case "RESET_FLOW":
      return initialState;
    default:
      return state;
  }
};

const ConnectorFlowContext = createContext<{
  state: ConnectorFlowState;
  dispatch: React.Dispatch<ConnectorFlowAction>;
} | null>(null);

export const ConnectorFlowProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(connectorFlowReducer, initialState);

  return (
    <ConnectorFlowContext.Provider value={{ state, dispatch }}>
      {children}
    </ConnectorFlowContext.Provider>
  );
};

export const useConnectorFlow = () => {
  const context = useContext(ConnectorFlowContext);
  if (!context) {
    throw new Error(
      "useConnectorFlow must be used within ConnectorFlowProvider"
    );
  }
  return context;
};
